import { Refine } from '@refinedev/core';
import { RefineSnackbarProvider, useNotificationProvider } from '@refinedev/mui';
import routerProvider from '@refinedev/react-router';
import dataProvider from '@refinedev/simple-rest';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { PaletteMode } from '@mui/material';
import { buildTheme, ColorModeContext, initialColorMode, persistColorMode } from './theme';
import { LanguageProvider } from './i18n';
import { LoginPage } from './pages/login';
import { AccessPage } from './pages/access';
import { DashboardPage } from './pages/dashboard';
import { AnnouncementsListPage } from './pages/announcements/list';
import { AnnouncementCreatePage } from './pages/announcements/create';
import { AnnouncementEditPage } from './pages/announcements/edit';
import { InvoicesListPage } from './pages/invoices/list';
import { InvoiceShowPage } from './pages/invoices/show';
import { InvoiceCreatePage } from './pages/invoices/create';
import { FacilitiesListPage } from './pages/facilities/list';
import { FacilityCreatePage } from './pages/facilities/create';
import { FacilityEditPage } from './pages/facilities/edit';
import { BookingsListPage } from './pages/bookings/list';
import { ComplaintsListPage } from './pages/complaints/list';
import { ComplaintShowPage } from './pages/complaints/show';
import { PaymentRemindersPage } from './pages/reminders/list';
import { UnitsListPage } from './pages/units/list';
import { PeopleSummaryPage } from './pages/people/summary';
import { ResidentsListPage } from './pages/residents/list';
import { OperationsPage } from './pages/operations/list';
import { ReportsPage } from './pages/reports/list';
import { FeesPage } from './pages/billing/fees';
import { PlatformDashboardPage } from './pages/platform/dashboard';
import { PlatformDevelopersPage } from './pages/platform/developers';
import { PlatformClustersPage } from './pages/platform/clusters';
import { PlatformArchivedPage } from './pages/platform/archived';
import { ResidentAppBrandingPage } from './pages/resident-app/branding';
import { ProtectedLayout } from './layout';
import { authProvider } from './auth-provider';
import { api } from './lib/api-client';
import { ClerkAuthBridge } from './lib/clerk-auth';
import { apiBaseUrl } from './runtime-config';

function App() {
  // Warm Stone theme with a persisted light/dark mode (see ./theme).
  const [mode, setMode] = useState<PaletteMode>(initialColorMode);
  const theme = useMemo(() => buildTheme(mode), [mode]);
  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((prev) => {
          const next: PaletteMode = prev === 'light' ? 'dark' : 'light';
          persistColorMode(next);
          return next;
        }),
    }),
    [mode],
  );

  // Bridge api-client's "logout" event (fired when refresh fails) to a hard redirect.
  useEffect(() => {
    const onLogout = () => {
      if (window.location.pathname !== '/login') window.location.href = '/login';
    };
    window.addEventListener('residenone:auth:logout', onLogout);
    return () => window.removeEventListener('residenone:auth:logout', onLogout);
  }, []);

  return (
    <LanguageProvider>
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RefineSnackbarProvider>
        <BrowserRouter>
          <ClerkAuthBridge />
          <Refine
            dataProvider={dataProvider(apiBaseUrl, api)}
            authProvider={authProvider}
            routerProvider={routerProvider}
            notificationProvider={useNotificationProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
              projectId: 'residenone-admin',
            }}
            // Resources are registered in subsequent phases as modules land.
            resources={[]}
          >
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/access" element={<AccessPage />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/announcements" element={<AnnouncementsListPage />} />
                <Route path="/announcements/new" element={<AnnouncementCreatePage />} />
                <Route path="/announcements/:id/edit" element={<AnnouncementEditPage />} />
                <Route path="/invoices" element={<InvoicesListPage />} />
                <Route path="/invoices/new" element={<InvoiceCreatePage />} />
                <Route path="/invoices/:id" element={<InvoiceShowPage />} />
                <Route path="/facilities" element={<FacilitiesListPage />} />
                <Route path="/facilities/new" element={<FacilityCreatePage />} />
                <Route path="/facilities/:id/edit" element={<FacilityEditPage />} />
                <Route path="/bookings" element={<BookingsListPage />} />
                <Route path="/complaints" element={<ComplaintsListPage />} />
                <Route path="/complaints/:id" element={<ComplaintShowPage />} />
                <Route path="/reminders" element={<PaymentRemindersPage />} />
                <Route path="/people/summary" element={<PeopleSummaryPage />} />
                <Route path="/units" element={<UnitsListPage />} />
                <Route path="/residents" element={<ResidentsListPage />} />
                <Route path="/operations" element={<OperationsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/billing/fees" element={<FeesPage />} />
                <Route path="/platform" element={<Navigate to="/platform/developers" replace />} />
                <Route path="/platform/dashboard" element={<PlatformDashboardPage />} />
                <Route path="/platform/developers" element={<PlatformDevelopersPage />} />
                <Route path="/platform/clusters" element={<PlatformClustersPage />} />
                <Route path="/platform/archived" element={<PlatformArchivedPage />} />
                <Route path="/users/team" element={<TeamRedirect />} />
                <Route path="/resident-app/branding" element={<ResidentAppBrandingPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Refine>
        </BrowserRouter>
      </RefineSnackbarProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
    </LanguageProvider>
  );
}

// The old "Anggota cluster" page is now the Tim pengelola tab of the Warga page; old links (and the
// developer/cluster/member they point at) land there.
function TeamRedirect() {
  const params = new URLSearchParams(useLocation().search);
  params.set('tab', 'team');
  return <Navigate to={`/residents?${params.toString()}`} replace />;
}

export default App;
